import { useEffect, useState } from 'react';
import { esScrollEdges } from '../../common/EsScrollEdges';
import { useEsStrings } from '../../common/EsStrings';

// The reader is "at the top" up to here. It is the footer's back-to-top threshold
// (themeNav.js shows that button past 50px), so the two buttons swap at one
// scroll position and are never both up or both down.
const TOP_ZONE = 50;

/*
   The home page's "more below" cue: the footer's back-to-top button turned over.
   Same box, ground, shadow, hover, and z-index, and the same bottom-right corner, so
   the one button changes direction instead of two of them sharing a screen.

   Not the `back-to-top` class: themeNav shows and hides everything that carries it
   and wires its click to scroll up. This one is shown from React state instead.

   On a phone the back-to-top button sits in the footer's flow rather than floating;
   this one floats at every width because at the top of the page the corner is free
   and the back-to-top button is not on screen to share it.
*/
export default function EsHomeScrollDown() {
    const t = useEsStrings();
    const [ shown, setShown ] = useState( false );

    useEffect( () => {
        const measure = () => {
            const aRoot = document.documentElement;
            const aY = window.scrollY || aRoot.scrollTop || 0;
            // Content below the fold, as EsScrollEdges counts it, on the page itself.
            const aMore = esScrollEdges( aY, aRoot.scrollHeight, aRoot.clientHeight ).bottom;
            setShown( aY <= TOP_ZONE && aMore );
        };
        measure();
        window.addEventListener( 'scroll', measure, { passive: true } );
        window.addEventListener( 'resize', measure );
        return () => {
            window.removeEventListener( 'scroll', measure );
            window.removeEventListener( 'resize', measure );
        };
    }, [] );

    const onClick = () => {
        // Read at click time: the setting can change while the page is open.
        const aStill = window.matchMedia
            && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
        const aBehavior = aStill ? 'auto' : 'smooth';
        // #features is the hero's own first button; its scroll-margin-top (EsHome.css)
        // keeps the heading out from under the fixed bar. One viewport is the fallback
        // for a page where that section is not drawn.
        const aNext = document.getElementById( 'features' );
        if ( aNext && typeof aNext.scrollIntoView === 'function' ) {
            aNext.scrollIntoView( { block: 'start', behavior: aBehavior } );
        } else {
            window.scrollBy( { top: window.innerHeight, behavior: aBehavior } );
        }
    };

    if ( !shown ) return null;

    return (
        <button type="button" onClick={ onClick }
                data-testid="es-home-scroll-down"
                aria-label={ t( 'home-scroll-down', 'Scroll down' ) }
                className="fixed inset-e-8 bottom-8 inset-s-auto flex h-10 w-10 items-center justify-center rounded-md bg-panel text-chrome-ink shadow-md transition duration-300 ease-in-out z-[999] hover:bg-panel-hover hover:shadow-register" >
            { /* The back-to-top chevron mirrored: the other two borders, and the
                 nudge toward the point's side moved from above to below. */ }
            <span className="h-3 w-3 rotate-45 border-b border-r border-chrome-ink mb-[6px]"></span>
        </button>
    );
}
